/**
 * OrderSearchAutocomplete Component
 * Searchable dropdown for selecting orders/jobs with customer-aware search.
 *
 * Features:
 * - Customer-scoped suggestions/search via `customerId`
 * - Server-backed search via `search` query param (minChars default=2)
 *   - Searches by order/job ID (e.g., "BUB-123")
 *   - Searches by customer name (partial match, e.g., "John")
 * - Optional auto-prepopulation when `selectedOrderId` is provided
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { LocalShipping, LocationOn } from "@mui/icons-material";
import { fetchOrders, fetchOrderById } from "../../features/orders/repositories/orderRepository.js";
import { logError } from "../../shared/utils/logger.js";

/**
 * OrderSearchAutocomplete Component
 * @param {Object} props
 * @param {Object|null} props.value - Selected order object
 * @param {Function} props.onChange - Callback when order is selected/changed
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.customerId] - Customer ID to filter recent orders
 */
const OrderSearchAutocomplete = ({
  value,
  onChange,
  label = "Search Order / Job (Required)",
  disabled = false,
  customerId = null,
  selectedOrderId = null,
  minChars = 2,
  restrictToCustomer = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Auto-prepopulate when an entity is already linked to an order/job
  useEffect(() => {
    let cancelled = false;

    async function prepopulate() {
      const targetId = String(selectedOrderId || "").trim();
      if (!targetId) return;

      // If the parent already supplied a value, nothing to do
      const currentId = value?.id || value?.jobId || null;
      if (currentId && String(currentId) === targetId) return;

      setLoading(true);
      try {
        const resp = await fetchOrderById(targetId);
        const order = resp?.data || null;
        if (!cancelled && order) {
          // If configured, ensure linked order belongs to customer before selecting
          if (
            restrictToCustomer &&
            customerId &&
            order.customerId &&
            String(order.customerId) !== String(customerId)
          ) {
            logError("OrderSearchAutocomplete: linked order does not belong to customer", {
              selectedOrderId: targetId,
              customerId,
              orderCustomerId: order.customerId,
            });
            return;
          }
          onChange(order);
        }
      } catch (err) {
        logError("OrderSearchAutocomplete: failed to prepopulate order", {
          selectedOrderId: targetId,
          error: err.message,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    prepopulate();
    return () => {
      cancelled = true;
    };
  }, [selectedOrderId, customerId, restrictToCustomer, value, onChange]);

  // Load recent orders for customer when customerId is available and input is empty/short
  useEffect(() => {
    let cancelled = false;

    async function loadRecentOrders() {
      // Only load recent orders if customerId is present and user hasn't typed much
      if ((restrictToCustomer && !customerId) || inputValue.trim().length >= minChars) {
        return;
      }

      setLoading(true);
      try {
        const response = await fetchOrders({
          customerId: restrictToCustomer ? customerId : undefined,
          limit: 10,
        });
        const orders = response?.data?.orders || response?.data || [];
        if (!cancelled) {
          setOptions(Array.isArray(orders) ? orders : []);
        }
      } catch (err) {
        logError("Error loading recent orders", {
          customerId,
          error: err.message,
        });
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRecentOrders();
    return () => {
      cancelled = true;
    };
  }, [customerId, inputValue, minChars, restrictToCustomer]);

  // Debounced search: server-backed search by id/jobId/customer name (after minChars)
  useEffect(() => {
    let cancelled = false;
    const term = inputValue.trim().toLowerCase();

    // Only search if user typed at least minChars characters
    if (term.length < minChars) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        // Fetch matching orders (optionally scoped to customer)
        const response = await fetchOrders({
          customerId: restrictToCustomer ? customerId || undefined : undefined,
          search: term,
          limit: 50,
        });

        const orders = response?.data?.orders || response?.data || [];
        const normalized = Array.isArray(orders) ? orders : [];

        if (!cancelled && normalized.length > 0) {
          setOptions(normalized);
          setLoading(false);
          return;
        }

        // Fallback: try direct lookup (works when full ID is typed)
        try {
          const directResponse = await fetchOrderById(inputValue.trim());
          const order = directResponse?.data || null;
          if (!cancelled) {
            setOptions(order ? [order] : []);
          }
        } catch (directErr) {
          // Direct lookup failed - no matches
          if (!cancelled) {
            setOptions([]);
          }
        }
      } catch (err) {
        logError("Error searching orders", {
          searchTerm: term,
          customerId,
          error: err.message,
        });
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [inputValue, customerId, minChars, restrictToCustomer]);

  // Format order display label
  const getOrderLabel = (order) => {
    if (!order) return "";
    const id = order.id || order.jobId || "Unknown";
    const customer = order.customer || order.customerName || "";
    const pickup = order.pickup?.address || "";
    const delivery = order.delivery?.address || "";
    
    const parts = [id];
    if (customer) parts.push(customer);
    if (pickup && delivery) {
      parts.push(`${pickup} → ${delivery}`);
    }
    
    return parts.join(" • ");
  };

  // Format order hint for option display
  const getOrderHint = (order) => {
    const parts = [];
    if (order.pickup?.address && order.delivery?.address) {
      parts.push(`${order.pickup.address} → ${order.delivery.address}`);
    } else if (order.pickup?.address) {
      parts.push(`From: ${order.pickup.address}`);
    } else if (order.delivery?.address) {
      parts.push(`To: ${order.delivery.address}`);
    }

    if (order.status) {
      parts.push(`Status: ${order.status}`);
    }

    return parts.length > 0 ? parts.join(" • ") : "No location/status info";
  };

  return (
    <Autocomplete
      disabled={disabled}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      getOptionLabel={getOrderLabel}
      isOptionEqualToValue={(option, value) => {
        return (
          (option?.id || option?.jobId) === (value?.id || value?.jobId)
        );
      }}
      loading={loading}
      filterOptions={(x) => x} // Disable client-side filtering, we do server-side
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={
            customerId && restrictToCustomer
              ? "Start typing an Order ID or customer name, or pick a recent order..."
              : "Type an Order ID or customer name (e.g., BUB-... or 'John')"
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const hint = getOrderHint(option);
        const orderId = option.id || option.jobId || "Unknown";

        return (
          <Box component="li" {...props} sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <LocalShipping fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  {orderId}
                </Typography>
                {option.status && (
                  <Chip
                    label={option.status}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: 3.5,
                  flexWrap: "wrap",
                }}
              >
                <LocationOn
                  fontSize="small"
                  color="action"
                  sx={{ fontSize: 14 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {hint}
                </Typography>
              </Box>
              {option.customer && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 3.5, display: "block" }}
                >
                  Customer: {option.customer}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      noOptionsText={
        inputValue.trim().length < minChars
          ? `Type at least ${minChars} characters to search`
          : "No orders found"
      }
    />
  );
};

export default OrderSearchAutocomplete;
